/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-08 15:41:22
 **/

import { CSSProperties, ComponentProps, useEffect, useRef, useState } from "react";

import { Avatar as A, Button, Icon, TypographyTooltip } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";

import { usePhoneCall, useSidebarActions } from "@/frameworks/core/hooks";
import { colors } from "@/frameworks/core/styles";
import { XMSClientManager } from "@/frameworks/core/unity";

import { Avatar } from "../../Avatar";
import * as S from "../emotion";

export type CallType = "calling" | "before_call" | "no_response" | "none";
interface UserVideoWithAvatarProps {
	callID: string;
	userID: string;
	callType?: CallType;
	kind: ComponentProps<typeof A>["kind"];
	src?: MediaProvider;
	isExpandIcon?: boolean;
	style?: CSSProperties;
	isSpeaking?: boolean;
	fullScreen?: boolean;
	onClick?: () => void;
}

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const LOGTAG = "#UserVideoWithAvatar#";

export default function UserVideoWithAvatar({
	userID,
	kind,
	callID,
	isExpandIcon,
	style,
	isSpeaking,
	callType = "none",
	fullScreen = false,
	onClick
}: UserVideoWithAvatarProps) {
	const [isHover, setIsHover] = useState(false);
	const phoneCall = usePhoneCall();
	const videoRef = useRef<HTMLVideoElement>(null);
	const sidebarActions = useSidebarActions();
	const call = phoneCall.state.call(callID)[0];
	const userInfo = userID && phoneCall.state.users(callID)[0]?.[userID];
	const callStatus = phoneCall.state.status(callID)[0];
	const localStream = XMSClientManager.get(callID)?.local_stream ?? null;
	const remoteStream = phoneCall.state.remote_stream(userID)[0];
	const micStatus = phoneCall.status(callID) ? phoneCall.get_user_media(callID, userID).audio : "off";
	const videoStatus = phoneCall.status(callID) ? phoneCall.get_user_media(callID, userID).video : "off";

	useEffect(() => {
		if (localStream && videoRef.current && userID === Configure.userID) {
			videoRef.current.srcObject = localStream;
			videoRef.current.muted = true;
			videoRef.current.onloadeddata = () => {
				const playPromise = videoRef.current?.play();
				if (playPromise) {
					playPromise
						.then(() => {
							console.debug(LOGTAG, "Local media has been played");
						})
						.catch((error) => {
							console.debug(LOGTAG, "Local media can't play", error);
						});
				}
			};
		}
	}, [localStream]);

	useEffect(() => {
		if (callStatus !== "ONGOING") return;
		if (remoteStream && videoRef.current && userID !== Configure.userID) {
			videoRef.current.srcObject = remoteStream;
			videoRef.current.muted = micStatus === "off";
			videoRef.current.onloadeddata = () => {
				const playPromise = videoRef.current?.play();
				if (playPromise) {
					playPromise
						.then(() => {
							console.debug(LOGTAG, "Remote media has been played");
						})
						.catch((error) => {
							console.debug(LOGTAG, "Remote media can't play", error);
						});
				} else {
					console.warn(LOGTAG, "Remote media can't play");
				}
			};
		}
	}, [remoteStream, callStatus]);

	useEffect(() => {
		if (callStatus === "END" && videoRef.current) {
			videoRef.current.srcObject = null;
		}
	}, [callStatus]);

	return (
		<S.AvatarContainer
			style={{ ...style }}
			onMouseEnter={() => setIsHover(true)}
			onMouseLeave={() => setIsHover(false)}
			onClick={onClick}
			callType={callType}
			isSpeaking={isSpeaking}
			isHover={isHover}
		>
			<S.AvatarWrapper fullScreen={fullScreen} callType={callType} isSpeaking={isSpeaking}>
				<S.Video id={`${userID} ${remoteStream?.id}`} ref={videoRef} autoPlay muted />
			</S.AvatarWrapper>
			<Maybe test={videoStatus === "off"}>
				<S.AvatarWrapper fullScreen={fullScreen} callType={callType} isSpeaking={isSpeaking}>
					{userID && <Avatar id={userID} kind={kind} showMiniProfile={false} />}
				</S.AvatarWrapper>
			</Maybe>
			<Maybe test={!fullScreen}>
				<S.LeftTopNameWrapper callType={callType} isSpeaking={isSpeaking}>
					<TypographyTooltip.div
						tooltipProps={{
							hasIcon: false
						}}
						componentProps={{
							typoSize: "Body3_MD",
							typoColor: "neutral_00_100",
							ellipsis: true,
							maxLine: 1,
							stretch: true,
							align: "left"
						}}
					>
						{userInfo && userInfo?.type === "USER" ? userInfo.name : userInfo && userInfo.callNumber}
					</TypographyTooltip.div>
				</S.LeftTopNameWrapper>
			</Maybe>
			{isHover && isExpandIcon && (
				<S.RightBottomIconWrapper>
					<Button.Icon
						size="Xlarge"
						name="Fullscreen24"
						style={{
							background: colors.neutral_00_68,
							color: "black"
						}}
						onClick={() => {
							if (sidebarActions.isOpen("PhoneDetail")) {
								sidebarActions.hide("PhoneDetail");
							}
							phoneCall.popup.show({
								callID: callID || "",
								callerID: call.callerId,
								uiType: "fullSizeShare",
								dominantUser: {
									userID,
									isScreen: false
								}
							});
						}}
					/>
				</S.RightBottomIconWrapper>
			)}
			<S.LeftBottomIconWrapper callType={callType} isSpeaking={isSpeaking}>
				<S.IconWrapper callType={callType} micStatus={micStatus}>
					<SwitchCase
						condition={callType}
						cases={{
							before_call: null,
							calling: <Icon name="Call0224" size={16} fill={colors.neutral_00_100} />,
							no_response: <Icon name="CallUnansweredTest24" fill="white" size={20} />,
							none: <>{micStatus !== "on" && <Icon name="MicOff24" size={20} fill="white" />}</>
						}}
					/>
				</S.IconWrapper>
			</S.LeftBottomIconWrapper>
			{callType === "before_call" && (
				<S.RightTopIconWrapper>
					<Icon name="Close24" size={20} fill="white" />
				</S.RightTopIconWrapper>
			)}
		</S.AvatarContainer>
	);
}
